import { useGridApiRef } from '@mui/x-data-grid-pro';
import type { GridApiPro } from '@mui/x-data-grid-pro';
import type { GridApiCommunity } from '@mui/x-data-grid/internals';
import { caseInsensitiveIncludes, vyDate } from '@vy/utils';
import React, { useState } from 'react';
import { useUnchargedUsageReport } from '../../../../hooks/requests/use-uncharged-usage-report';
import { useSearchWithSearchParams } from '../../../../hooks/use-search-with-search-params';
import { ReportHeader } from '../../reports-components/ReportHeader';
import { ReportTable } from '../../reports-components/ReportTable';
import { REPORT_TYPES } from '../../reports-page/reports-page-types/report-type';
import { UnchargedUsageReportToolbar } from './UnchargedUsageReportToolbar';
import { UnchargedUsageReportTable } from './uncharged-usage-report-table/UnchargedUsageTable';

type UnchargedUsageReportContentProps = {
  reportId: string;
};

export const UnchargedUsageReportContent: React.FC<UnchargedUsageReportContentProps> = ({ reportId }) => {
  const { data: unchargedReportData, isLoading } = useUnchargedUsageReport(reportId);
  const [searchText, setSearchText] = useSearchWithSearchParams();
  const { report, creationDate } = unchargedReportData;
  const apiRef = useGridApiRef();

  const [selectedMonth, setSelectedMonth] = useState<Date | null>(null);
  const [customerAliasFilter, setCustomerAliasFilter] = useState('');

  const filteredReport = report.filter((row) => {
    if (selectedMonth !== null) {
      const rowMonth = vyDate(row.period.startTime);
      const isMatchingMonth = rowMonth.isSame(selectedMonth, 'month');
      if (!isMatchingMonth) return false;
    }

    if (customerAliasFilter !== '') {
      const matchesCustomer = caseInsensitiveIncludes(row.customerName, customerAliasFilter);
      if (!matchesCustomer) return false;
    }

    return true;
  });

  return (
    <>
      <ReportHeader reportData={report} creationDate={creationDate} />

      <ReportTable
        title="Uncharged Usage Report"
        placeholder="Enter product, customer name..."
        exportCsvFileName="Uncharged usage report"
        reportType={REPORT_TYPES.UNCHARGED_USAGE}
        reportCreationDate={creationDate}
        onSearch={(searchValue) => setSearchText(searchValue)}
        apiRef={apiRef as React.MutableRefObject<GridApiCommunity>}
      >
        <UnchargedUsageReportToolbar
          selectedMonth={selectedMonth}
          onMonthChange={setSelectedMonth}
          customerAlias={customerAliasFilter}
          onCustomerAliasChange={setCustomerAliasFilter}
        />

        <UnchargedUsageReportTable
          isLoading={isLoading}
          filter={searchText ?? undefined}
          apiRef={apiRef as React.MutableRefObject<GridApiPro>}
          rows={filteredReport}
        />
      </ReportTable>
    </>
  );
};
